import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import ToolLayout from 'tools/ToolLayout';
import Colors from 'theme/colors';
import {
  audioBufferToWav,
  defaultRecordFilename,
  formatElapsed,
  pickRecorderMimeType,
  supportsDisplayMediaAudio,
} from './wav';

type SourceMode = 'mic' | 'display';
type RecState = 'idle' | 'recording' | 'paused' | 'stopped';

type MicDevice = {
  deviceId: string;
  label: string;
};

const card: any = {
  backgroundColor: Colors.WHITE,
  border: `1px solid ${Colors.CARD_BORDER}`,
  borderRadius: 12,
  padding: 16,
  marginBottom: 14,
};

const label: any = {
  display: 'block',
  fontSize: 13,
  fontWeight: 700,
  color: Colors.MAIN_COLOR,
  marginBottom: 8,
};

const muted: any = {
  fontSize: 13,
  color: Colors.MUTED_TEXT,
  lineHeight: '20px',
  margin: '8px 0 0',
};

const errorBox: any = {
  ...card,
  borderColor: '#F5C2C7',
  backgroundColor: '#FFF5F5',
  color: '#842029',
  fontSize: 14,
};

const row: any = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: 8,
  marginTop: 12,
};

const btnBase: any = {
  borderRadius: 10,
  padding: '10px 16px',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
  border: 'none',
  minWidth: 110,
};

const primaryBtn = (disabled?: boolean): any => ({
  ...btnBase,
  backgroundColor: disabled ? Colors.SILVER_CHALICE : Colors.MAIN_COLOR,
  color: Colors.WHITE,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const secondaryBtn = (disabled?: boolean): any => ({
  ...btnBase,
  backgroundColor: Colors.WHITE,
  color: Colors.MAIN_COLOR,
  border: `1.5px solid ${Colors.MAIN_COLOR}`,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});

const dangerBtn: any = {
  ...btnBase,
  backgroundColor: '#C62828',
  color: Colors.WHITE,
};

const sourceBtn = (active: boolean, disabled?: boolean): any => ({
  ...btnBase,
  flex: 1,
  minWidth: 140,
  backgroundColor: active ? Colors.MAIN_COLOR : Colors.WHITE,
  color: active ? Colors.WHITE : Colors.MAIN_COLOR,
  border: `1.5px solid ${Colors.MAIN_COLOR}`,
  opacity: disabled ? 0.45 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const timerStyle: any = {
  fontSize: 36,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: Colors.TEXT,
  textAlign: 'center',
  margin: '8px 0',
};

const meterTrack: any = {
  height: 10,
  backgroundColor: Colors.SURFACE,
  borderRadius: 6,
  overflow: 'hidden',
  border: `1px solid ${Colors.CARD_BORDER}`,
};

const inputStyle: any = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  fontSize: 15,
  borderRadius: 8,
  border: `1px solid ${Colors.CARD_BORDER}`,
  marginTop: 4,
};

const selectStyle: any = {
  ...inputStyle,
  backgroundColor: Colors.WHITE,
  cursor: 'pointer',
};

const getMediaDevicesApi = () => {
  const g: any = typeof global !== 'undefined' ? global : {};
  const nav = (g.window || g).navigator;
  return nav && nav.mediaDevices ? nav.mediaDevices : null;
};

const Recorder: FC = () => {
  const [source, setSource] = useState<SourceMode>('mic');
  const [state, setState] = useState<RecState>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [wavBlob, setWavBlob] = useState<any>(null);
  const [filename, setFilename] = useState(defaultRecordFilename());
  const [converting, setConverting] = useState(false);
  const [micDevices, setMicDevices] = useState<MicDevice[]>([]);
  const [selectedMicId, setSelectedMicId] = useState('');
  const [micLabelsHidden, setMicLabelsHidden] = useState(false);
  const [refreshingMics, setRefreshingMics] = useState(false);
  const displaySupported = supportsDisplayMediaAudio();

  const mediaRecorderRef = useRef<any>(null);
  const streamRef = useRef<any>(null);
  const chunksRef = useRef<any[]>([]);
  const timerRef = useRef<any>(null);
  const startedAtRef = useRef(0);
  const pausedAccumRef = useRef(0);
  const pauseStartedRef = useRef(0);
  const audioCtxRef = useRef<any>(null);
  const analyserRef = useRef<any>(null);
  const rafRef = useRef<number | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopMeter = () => {
    const g: any = typeof global !== 'undefined' ? global : {};
    const win = g.window || g;
    if (rafRef.current != null && win.cancelAnimationFrame) {
      win.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setLevel(0);
  };

  const cleanupStream = () => {
    stopMeter();
    if (audioCtxRef.current) {
      try {
        audioCtxRef.current.close();
      } catch {
        /* ignore */
      }
      audioCtxRef.current = null;
      analyserRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  };

  const revokePreview = () => {
    const g: any = typeof global !== 'undefined' ? global : {};
    const URLApi = (g.window && g.window.URL) || g.URL;
    if (previewUrlRef.current && URLApi) {
      URLApi.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setWavBlob(null);
  };

  const loadMicDevices = useCallback(async () => {
    const mediaDevices = getMediaDevicesApi();
    if (!mediaDevices || !mediaDevices.enumerateDevices) {
      setMicDevices([]);
      return;
    }
    try {
      const devices = await mediaDevices.enumerateDevices();
      const inputs: MicDevice[] = devices
        .filter((d: any) => d.kind === 'audioinput')
        .map((d: any, index: number) => ({
          deviceId: d.deviceId,
          label: d.label || `Micro ${index + 1}`,
        }));
      setMicDevices(inputs);
      setMicLabelsHidden(inputs.some((d) => /^Micro \d+$/.test(d.label)));
      if (selectedMicId && !inputs.some((d) => d.deviceId === selectedMicId)) {
        setSelectedMicId('');
      }
    } catch {
      setMicDevices([]);
    }
  }, [selectedMicId]);

  const refreshMicDevicesWithPermission = useCallback(async () => {
    const mediaDevices = getMediaDevicesApi();
    if (!mediaDevices || !mediaDevices.getUserMedia) {
      setError('Trình duyệt không hỗ trợ chọn micro.');
      return;
    }
    setRefreshingMics(true);
    setError(null);
    try {
      const constraints: any = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
        video: false,
      };
      if (selectedMicId) {
        constraints.audio.deviceId = { exact: selectedMicId };
      }
      const stream = await mediaDevices.getUserMedia(constraints);
      stream.getTracks().forEach((t: any) => t.stop());
      await loadMicDevices();
    } catch (e: any) {
      const name = e && e.name;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setError('Bạn đã từ chối quyền truy cập micro.');
      } else if (name === 'NotFoundError') {
        setError('Không tìm thấy thiết bị micro đã chọn.');
      } else {
        setError(e && e.message ? String(e.message) : 'Không cập nhật được danh sách micro.');
      }
    } finally {
      setRefreshingMics(false);
    }
  }, [loadMicDevices, selectedMicId]);

  useEffect(() => {
    loadMicDevices();
    const mediaDevices = getMediaDevicesApi();
    if (!mediaDevices || !mediaDevices.addEventListener) {
      return () => {
        clearTimer();
        cleanupStream();
        revokePreview();
      };
    }
    const onDeviceChange = () => {
      loadMicDevices();
    };
    mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => {
      mediaDevices.removeEventListener('devicechange', onDeviceChange);
      clearTimer();
      cleanupStream();
      revokePreview();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMicDevices]);

  const startMeter = (stream: any) => {
    try {
      const g: any = typeof global !== 'undefined' ? global : {};
      const win = g.window || g;
      const AC = win.AudioContext || win.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const sourceNode = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      sourceNode.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setLevel(Math.min(1, rms * 3));
        rafRef.current = win.requestAnimationFrame(tick);
      };
      rafRef.current = win.requestAnimationFrame(tick);
    } catch {
      /* meter is optional */
    }
  };

  const convertChunksToWav = useCallback(async (chunks: any[], mimeType: string) => {
    setConverting(true);
    setError(null);
    try {
      const g: any = typeof global !== 'undefined' ? global : {};
      const win = g.window || g;
      const BlobCtor = win.Blob;
      const URLApi = win.URL;
      const blob = new BlobCtor(chunks, { type: mimeType || 'audio/webm' });
      const arrayBuffer = await blob.arrayBuffer();
      const AC = win.AudioContext || win.webkitAudioContext;
      const ctx = new AC();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
      await ctx.close();
      const wav = audioBufferToWav(audioBuffer);
      setWavBlob(wav);
      if (previewUrlRef.current) URLApi.revokeObjectURL(previewUrlRef.current);
      const url = URLApi.createObjectURL(wav);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (e) {
      setError('Không chuyển được bản ghi sang WAV. Thử ghi lại với trình duyệt Chrome/Edge.');
    } finally {
      setConverting(false);
    }
  }, []);

  const handleStop = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || (recorder.state !== 'recording' && recorder.state !== 'paused')) {
      return;
    }
    clearTimer();
    stopMeter();
    recorder.stop();
  }, []);

  const startRecording = async () => {
    setError(null);
    revokePreview();
    setFilename(defaultRecordFilename());
    chunksRef.current = [];

    const g: any = typeof global !== 'undefined' ? global : {};
    const win = g.window || g;
    const nav = win.navigator;
    const MediaRec = win.MediaRecorder;

    if (!MediaRec) {
      setError('Trình duyệt không hỗ trợ MediaRecorder.');
      return;
    }

    try {
      let stream: any;
      if (source === 'mic') {
        const audioConstraints: any = {
          echoCancellation: true,
          noiseSuppression: true,
        };
        if (selectedMicId) {
          audioConstraints.deviceId = { exact: selectedMicId };
        }
        stream = await nav.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false,
        });
        await loadMicDevices();
      } else {
        if (!displaySupported) {
          setError('Ghi âm máy tính/tab chỉ hỗ trợ trên Chrome hoặc Edge.');
          return;
        }
        stream = await nav.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });
        const audioTracks = stream.getAudioTracks();
        if (!audioTracks.length) {
          stream.getTracks().forEach((t: any) => t.stop());
          setError(
            'Không có track âm thanh. Khi chia sẻ, hãy chọn tab và bật “Share tab audio” / “Chia sẻ âm thanh tab”.'
          );
          return;
        }
        stream.getVideoTracks().forEach((t: any) => {
          t.stop();
          stream.removeTrack(t);
        });
      }

      streamRef.current = stream;
      stream.getAudioTracks().forEach((track: any) => {
        track.onended = () => {
          handleStop();
        };
      });

      const mimeType = pickRecorderMimeType();
      const options: any = mimeType ? { mimeType } : {};
      const recorder = new MediaRec(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (ev: any) => {
        if (ev.data && ev.data.size > 0) {
          chunksRef.current.push(ev.data);
        }
      };

      recorder.onstop = async () => {
        const usedMime = recorder.mimeType || mimeType || 'audio/webm';
        const chunks = chunksRef.current.slice();
        cleanupStream();
        setState('stopped');
        await convertChunksToWav(chunks, usedMime);
      };

      recorder.start(1000);
      startedAtRef.current = Date.now();
      pausedAccumRef.current = 0;
      setElapsedMs(0);
      setState('recording');
      startMeter(stream);

      clearTimer();
      timerRef.current = setInterval(() => {
        const now = Date.now();
        setElapsedMs(now - startedAtRef.current - pausedAccumRef.current);
      }, 200);
    } catch (e: any) {
      cleanupStream();
      const name = e && e.name;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setError('Bạn đã từ chối quyền truy cập micro / chia sẻ màn hình.');
      } else if (name === 'NotFoundError') {
        setError('Không tìm thấy thiết bị micro.');
      } else {
        setError(e && e.message ? String(e.message) : 'Không bắt đầu được bản ghi.');
      }
      setState('idle');
    }
  };

  const pauseRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.pause();
    pauseStartedRef.current = Date.now();
    clearTimer();
    stopMeter();
    setState('paused');
  };

  const resumeRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;
    pausedAccumRef.current += Date.now() - pauseStartedRef.current;
    recorder.resume();
    setState('recording');
    if (streamRef.current) startMeter(streamRef.current);
    clearTimer();
    timerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current - pausedAccumRef.current);
    }, 200);
  };

  const downloadWav = () => {
    if (!wavBlob) return;
    const name = (filename || defaultRecordFilename()).replace(/\.wav$/i, '');
    const g: any = typeof global !== 'undefined' ? global : {};
    const win = g.window || g;
    const doc = (win && win.document) || g.document;
    const a = doc.createElement('a');
    a.href = win.URL.createObjectURL(wavBlob);
    a.download = `${name}.wav`;
    doc.body.appendChild(a);
    a.click();
    doc.body.removeChild(a);
  };

  const resetAll = () => {
    clearTimer();
    cleanupStream();
    revokePreview();
    setElapsedMs(0);
    setState('idle');
    setError(null);
    setFilename(defaultRecordFilename());
    chunksRef.current = [];
  };

  const busy = state === 'recording' || state === 'paused';

  return (
    <ToolLayout title="Ghi âm — Language123 Tools">
      <div style={card}>
        <span style={label}>Nguồn ghi</span>
        <div style={row}>
          <button
            type="button"
            style={sourceBtn(source === 'mic', busy)}
            disabled={busy}
            onClick={() => setSource('mic')}
          >
            Micro
          </button>
          <button
            type="button"
            style={sourceBtn(source === 'display', busy || !displaySupported)}
            disabled={busy || !displaySupported}
            onClick={() => setSource('display')}
            title={
              displaySupported
                ? 'Ghi âm thanh tab/màn hình (Chrome/Edge)'
                : 'Chỉ hỗ trợ Chrome/Edge'
            }
          >
            Âm thanh máy tính
          </button>
        </div>
        {source === 'mic' ? (
          <>
            <label style={{ ...label, marginTop: 12 }} htmlFor="rec-mic-device">
              Chọn micro
            </label>
            <select
              id="rec-mic-device"
              style={selectStyle}
              value={selectedMicId}
              disabled={busy || refreshingMics || micDevices.length === 0}
              onChange={(e: any) => setSelectedMicId(e.target.value)}
            >
              <option value="">Mặc định của hệ thống</option>
              {micDevices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </option>
              ))}
            </select>
            {micLabelsHidden ? (
              <p style={muted}>
                Tên thiết bị chưa hiển thị — nhấn &quot;Cập nhật danh sách&quot; hoặc &quot;Ghi âm&quot; để
                cho phép micro.
              </p>
            ) : null}
            <div style={row}>
              <button
                type="button"
                style={secondaryBtn(busy || refreshingMics)}
                disabled={busy || refreshingMics}
                onClick={refreshMicDevicesWithPermission}
              >
                {refreshingMics ? 'Đang cập nhật…' : 'Cập nhật danh sách'}
              </button>
            </div>
          </>
        ) : null}
        <p style={muted}>
          {source === 'mic'
            ? 'Ghi từ micro tích hợp, USB mic hoặc tai nghe (AirPods…).'
            : 'Chia sẻ tab/cửa sổ và bật chia sẻ âm thanh. Chỉ hoạt động tốt trên Chrome/Edge.'}
        </p>
        {!displaySupported && (
          <p style={muted}>
            Trình duyệt hiện tại không hỗ trợ ghi âm thanh máy tính qua getDisplayMedia.
          </p>
        )}
      </div>

      <div style={card}>
        <div style={timerStyle}>{formatElapsed(elapsedMs)}</div>
        <div style={meterTrack} aria-hidden>
          <div
            style={{
              height: '100%',
              width: `${Math.round(level * 100)}%`,
              backgroundColor: state === 'recording' ? Colors.SUCCESS : Colors.MAIN_COLOR_2,
              transition: 'width 80ms linear',
            }}
          />
        </div>
        <div style={row}>
          {state === 'idle' || state === 'stopped' ? (
            <button type="button" style={primaryBtn(converting)} disabled={converting} onClick={startRecording}>
              Ghi âm
            </button>
          ) : null}
          {state === 'recording' ? (
            <>
              <button type="button" style={secondaryBtn()} onClick={pauseRecording}>
                Tạm dừng
              </button>
              <button type="button" style={dangerBtn} onClick={handleStop}>
                Dừng
              </button>
            </>
          ) : null}
          {state === 'paused' ? (
            <>
              <button type="button" style={primaryBtn()} onClick={resumeRecording}>
                Tiếp tục
              </button>
              <button type="button" style={dangerBtn} onClick={handleStop}>
                Dừng
              </button>
            </>
          ) : null}
          {(state === 'stopped' || state === 'idle') && (previewUrl || elapsedMs > 0) ? (
            <button type="button" style={secondaryBtn()} onClick={resetAll}>
              Ghi mới
            </button>
          ) : null}
        </div>
        <p style={muted}>
          Bản ghi được giữ tạm trong bộ nhớ trình duyệt. Nên dừng và tải về nếu ghi lâu.
        </p>
      </div>

      {error ? <div style={errorBox}>{error}</div> : null}

      {(converting || previewUrl) && (
        <div style={card}>
          <span style={label}>Kết quả</span>
          {converting ? (
            <p style={muted}>Đang chuyển sang WAV…</p>
          ) : (
            <>
              {previewUrl ? (
                <audio controls src={previewUrl} style={{ width: '100%', marginTop: 4 }} />
              ) : null}
              <label style={{ ...label, marginTop: 12 }} htmlFor="rec-filename">
                Tên file
              </label>
              <input
                id="rec-filename"
                style={inputStyle}
                value={filename}
                onChange={(e: any) => setFilename(e.target.value)}
              />
              <div style={row}>
                <button
                  type="button"
                  style={primaryBtn(!wavBlob)}
                  disabled={!wavBlob}
                  onClick={downloadWav}
                >
                  Tải về WAV
                </button>
                <button type="button" style={secondaryBtn()} onClick={resetAll}>
                  Ghi mới
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </ToolLayout>
  );
};

export default Recorder;
